import SvgChevronright from '@common/__generated__/icon/SvgChevronright';
import { SvgVisible } from '@common/icon';
import { cn } from '@core/utils';

import { CustomLink } from '@/app/_components/CustomLink';
import { NewBadge } from '@/app/donation/_components/NewBadge';
import { Rating } from '@/app/donation/_components/Rating';
import { Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';

import type { WelloPickItem } from '../../types';

type PickedGiftProps = {
  onClickItem: () => void;
  isDisplayInstitution: boolean;
  priority?: boolean;
  className?: string;
} & WelloPickItem;

export const PickedGift = ({
  isDisplayInstitution,
  onClickItem,
  productNumber,
  productName,
  imageUrl,
  isNew,
  readCount,
  institutionId,
  institutionName,
  institutionLogoUrl,
  reviewRating,
  reviewCount,
  className,
  donationAmount,
}: PickedGiftProps) => {
  return (
    <article className={cn('w-224 shrink-0', 'mobile-tablet:w-140', className)}>
      {/* 상품 이미지 */}
      <CustomLink
        href={DONATION_ROUTES.GIFTS_DETAIL.pathname({
          productNumber: productNumber,
        })}
        aria-label={`${productName} 상세페이지로 이동`}
        className={cn(
          'relative mb-12 block h-[21.25rem] w-full',
          'mobile-tablet:mb-8 mobile-tablet:h-210',
        )}
        onClick={onClickItem}
      >
        <Image
          fill
          className="rounded-xlarge object-cover"
          src={imageUrl}
          sizes={`${BREAKPOINTS.MOBILE_TABLET} 140px, 224px`}
          alt={productName}
        />
        <div className="absolute left-0 top-0 flex items-center">
          <Image
            src="https://cdn.welfarehello.com/static/donation/main/pick.png"
            alt="pick"
            width={45}
            height={24}
          />
          {isNew ? <NewBadge /> : null}
        </div>

        <div className="absolute bottom-0 left-0 flex w-full items-center justify-center gap-2 truncate rounded-b-xlarge bg-gradient-to-t from-Gray-black/40 to-transparent pb-12">
          <SvgVisible className="size-14 text-Gray-white" />
          <p className="font-600 text-Gray-white typo-caption1">
            {readCount?.toLocaleString()}
            명이 보는 중
          </p>
        </div>

        <div className="to-black/40 absolute inset-0 size-full bg-gradient-to-b from-transparent from-[73.33%]" />
      </CustomLink>

      {/** 지자체 정보 */}
      {isDisplayInstitution && (
        <CustomLink
          href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
            id: institutionId,
          })}
          className="flex items-center gap-4 pb-4"
        >
          <div
            className={cn(
              'relative size-24 overflow-hidden rounded-pill border border-Gray-300',
              'mobile-tablet:size-20',
            )}
          >
            <Image
              fill
              src={institutionLogoUrl}
              alt={institutionName}
              className="object-cover"
              sizes={`${BREAKPOINTS.MOBILE_TABLET} 20px, 24px`}
            />
          </div>
          <p
            className={cn(
              'pt-2 font-600 text-Gray-600 typo-body4',
              'mobile-tablet:font-600 mobile-tablet:typo-caption1',
            )}
          >
            {institutionName}
          </p>
          <SvgChevronright
            className={cn('size-16 text-Gray-600', 'mobile-tablet:size-12')}
          />
        </CustomLink>
      )}

      {/** 상품명*/}
      <CustomLink
        className={cn(
          'mb-8 line-clamp-2 font-500 text-Gray-800 typo-body3',
          'mobile-tablet:font-500 mobile-tablet:typo-body4',
        )}
        href={DONATION_ROUTES.GIFTS_DETAIL.pathname({
          productNumber: productNumber,
        })}
        aria-label={`${productName} 상세페이지로 이동`}
        onClick={onClickItem}
      >
        {productName}
      </CustomLink>

      <div
        className={cn('mb-2 flex items-center gap-4', 'mobile-tablet:hidden')}
      >
        <p className="font-700 text-Gray-black typo-body2">
          {donationAmount.toLocaleString()}
          <span className="font-600 typo-body3">원</span>
        </p>
        <p className="font-600 text-Gray-550 typo-body3">기부시 증정</p>
      </div>

      {/** 평점 */}
      <Rating value={reviewRating} count={reviewCount} size={12} />
    </article>
  );
};
